import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, CircleDollarSign, Package, MoreVertical, Layers, CheckCircle2, Clock, Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatCurrency } from "@/utils/currency";

import { useNavigate } from "react-router-dom";

interface CollaborationsProps {
  influencerId: string;
}

export default function Collaborations({ influencerId }: CollaborationsProps) {
  const navigate = useNavigate();
  const { userCountry } = useAuth();
  const { data: collaborations = [], isLoading } = useQuery({
    queryKey: ['influencer-collaborations', influencerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaign_influencers')
        .select(`
          *,
          campaigns (
            id,
            name,
            start_date,
            end_date,
            status,
            total_budget,
            brands (
              name
            )
          ),
          payments (
            amount,
            status
          )
        `)
        .eq('influencer_id', influencerId);

      if (error) throw error;
      return data || [];
    },
  });

  interface PaymentItem {
    amount: number | null;
    status: string;
  }

  const getPaymentStatus = (payments: PaymentItem[]) => {
    if (!payments || payments.length === 0) return 'Not Set';
    const totalAmount = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
    const paidAmount = payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + (p.amount || 0), 0);

    if (paidAmount === 0) return 'Pending';
    if (paidAmount >= totalAmount) return 'Paid';
    return 'Partial';
  };

  const getTotalPayment = (payments: PaymentItem[]) => {
    if (!payments || payments.length === 0) return 0;
    return payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  };

  const filterCollaborations = (filter: string) => {
    switch (filter) {
      case 'completed':
        return collaborations.filter(c => c.campaigns?.status === 'completed');
      case 'in-progress':
        return collaborations.filter(c => c.campaigns?.status === 'active');
      case 'invited':
        return collaborations.filter(c => c.status === 'contacted');
      default:
        return collaborations;
    }
  };

  const formatDateShort = (dateStr?: string) => {
    if (!dateStr) return "TBD";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-6 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#A655F8]/10 text-[#A655F8] flex items-center justify-center">
            <Package className="w-5 h-5" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">Past Collaborations</h2>
        </div>
        <div className="text-center text-gray-500 py-8">Loading collaborations...</div>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 bg-white border border-slate-200 rounded-2xl shadow-sm p-6 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-[#A655F8]/10 text-[#A655F8] flex items-center justify-center">
          <Package className="w-5 h-5" />
        </div>
        <h2 className="text-xl font-bold text-slate-900 tracking-tight">Past Collaborations</h2>
      </div>

      <Tabs defaultValue="all" className="w-full space-y-4">
        <TabsList className="bg-white p-0 rounded-xl flex items-stretch w-full sm:w-fit border border-slate-200 h-12 overflow-x-auto scrollbar-none justify-start whitespace-nowrap">
          <TabsTrigger
            value="all"
            className="flex-1 sm:flex-initial h-full px-5 flex items-center justify-center gap-2 text-xs sm:text-sm font-medium text-slate-600 transition-all border-b-2 border-transparent data-[state=active]:border-[#A655F8] data-[state=active]:text-[#A655F8] data-[state=active]:font-semibold data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
          >
            <Layers className="w-4 h-4 shrink-0" />
            All ({collaborations.length})
          </TabsTrigger>
          <div className="h-5 w-px bg-slate-200 self-center shrink-0" />
          <TabsTrigger
            value="completed"
            className="flex-1 sm:flex-initial h-full px-5 flex items-center justify-center gap-2 text-xs sm:text-sm font-medium text-slate-600 transition-all border-b-2 border-transparent data-[state=active]:border-[#A655F8] data-[state=active]:text-[#A655F8] data-[state=active]:font-semibold data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
          >
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            Completed ({filterCollaborations('completed').length})
          </TabsTrigger>
          <div className="h-5 w-px bg-slate-200 self-center shrink-0" />
          <TabsTrigger
            value="in-progress"
            className="flex-1 sm:flex-initial h-full px-5 flex items-center justify-center gap-2 text-xs sm:text-sm font-medium text-slate-600 transition-all border-b-2 border-transparent data-[state=active]:border-[#A655F8] data-[state=active]:text-[#A655F8] data-[state=active]:font-semibold data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
          >
            <Clock className="w-4 h-4 shrink-0" />
            In Progress ({filterCollaborations('in-progress').length})
          </TabsTrigger>
          <div className="h-5 w-px bg-slate-200 self-center shrink-0" />
          <TabsTrigger
            value="invited"
            className="flex-1 sm:flex-initial h-full px-5 flex items-center justify-center gap-2 text-xs sm:text-sm font-medium text-slate-600 transition-all border-b-2 border-transparent data-[state=active]:border-[#A655F8] data-[state=active]:text-[#A655F8] data-[state=active]:font-semibold data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
          >
            <Mail className="w-4 h-4 shrink-0" />
            Invited ({filterCollaborations('invited').length})
          </TabsTrigger>
        </TabsList>

        {['all', 'completed', 'in-progress', 'invited'].map((filter) => (
          <TabsContent key={filter} value={filter} className="space-y-4">
            {filterCollaborations(filter).length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                No {filter === 'all' ? '' : filter} collaborations found.
              </div>
            ) : (
              filterCollaborations(filter).map((collab) => (
                <div key={collab.id} className="relative border border-slate-200 rounded-2xl pt-2 pb-6 px-6 space-y-4 bg-white">
                  {/* Action dropdown button */}
                  <div className="absolute top-4 right-6">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-600 rounded-full">
                      <MoreVertical className="w-5 h-5" />
                    </Button>
                  </div>

                  {/* Campaign title & brand name */}
                  <div className="space-y-1 pr-1">
                    <h3
                      onClick={() => collab.campaigns?.id && navigate(`/campaigns/${collab.campaigns.id}`)}
                      className="text-xl font-bold text-slate-900 hover:text-[#A655F8] cursor-pointer transition-colors tracking-tight"
                    >
                      {collab.campaigns?.name}
                    </h3>
                    <p className="text-sm font-semibold text-slate-800">{collab.campaigns?.brands?.name}</p>
                  </div>

                  {/* Campaign info details */}
                  <div className="flex flex-wrap items-center gap-10 text-sm text-slate-600 font-medium pt-1">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-800" />
                      <span className="text-slate-800">
                        {formatDateShort(collab.campaigns?.start_date)} - {formatDateShort(collab.campaigns?.end_date)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 ml-16">
                      <CircleDollarSign className="w-4 h-4 text-slate-800" />
                      <span className="text-slate-800">{formatCurrency(collab.campaigns?.total_budget || 0, userCountry)}</span>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="w-full h-px bg-slate-300 my-4" />

                  {/* Deliverables & Payment Status Badge */}
                  <div className="flex items-center justify-between gap-2">
                    {collab.deliverables && (
                      <div className="text-sm font-medium text-slate-600">
                        <span className="font-semibold text-slate-800">Deliverables: </span>
                        {collab.deliverables}
                      </div>
                    )}
                    <div className="ml-auto shrink-0">
                      <span className={`inline-flex px-3 py-1 rounded-xl text-sm font-bold uppercase tracking-wider shadow-sm ${getPaymentStatus(collab.payments) === 'Paid'
                        ? 'bg-[#0F172A] text-white'
                        : 'bg-slate-300 text-slate-700'
                        }`}>
                        {getPaymentStatus(collab.payments)}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
